import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { formatCurrency } from '../../utils/format';
import { X, FileDown, FileText, Settings } from 'lucide-react';
import * as XLSX from 'xlsx';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import CoproManagementModal from './CoproManagementModal';
import { dateRangesFilter, getMonthRangeDates, getMonthsLabel, getPeriodRanges } from '../../utils/period';

interface CoproTransaction {
  id: string;
  property_number: string;
  seller: string;
  buyer: string;
  sale_price: number;
  fees_with_tax: number;
  fees_without_tax: number;
  status: string;
  compromise_date: string;
  agencies: string[];
  negotiators: {
    name: string;
    role: string;
    percentage: number;
  }[];
  retrocessions: {
    type: string;
    amount: number;
  }[];
}

interface CoproStats {
  numero: string;
  residence: string;
  transaction_count: number;
  total_sales: number;
  total_fees: number;
  total_retrocessions: number;
  transactions: CoproTransaction[];
}

type CoproType = 'lefeuvre' | 'newco' | 'syndic';

interface CoproTableProps {
  type: CoproType;
  period: 'year' | 'quarter' | 'month';
  selectedPeriod: string;
  fiscalYear: number;
}

export default function CoproTable({ type, period, selectedPeriod, fiscalYear }: CoproTableProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copros, setCopros] = useState<CoproStats[]>([]);
  const [selectedCopro, setSelectedCopro] = useState<CoproStats | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [isManagementModalOpen, setIsManagementModalOpen] = useState(false);

  const getConfig = () => {
    switch (type) {
      case 'lefeuvre':
        return {
          tableName: 'copros',
          foreignKey: 'copro_id',
          retrocessionType: 'retrocession_lefeuvre',
          title: 'Copropriétés Lefeuvre',
          color: 'blue'
        };
      case 'newco':
        return {
          tableName: 'copros_newco',
          foreignKey: 'copro_newco_id',
          retrocessionType: 'retrocession_newco',
          title: 'Copropriétés NewCo',
          color: 'green'
        };
      case 'syndic':
        return {
          tableName: 'copros_syndic',
          foreignKey: 'copro_syndic_id',
          retrocessionType: 'retrocession_syndic',
          title: 'Copropriétés Kermarrec Syndic',
          color: 'purple'
        };
    }
  };

  const config = getConfig();

  useEffect(() => {
    loadData();
  }, [type, period, selectedPeriod, fiscalYear]);

  const getFormattedPeriod = () => {
    if (period === 'year') {
      return `Année ${fiscalYear}-${fiscalYear + 1}`;
    } else if (period === 'quarter') {
      const quarterMap = {
        '1': 'Trimestre 1 (Avril - Juin)',
        '2': 'Trimestre 2 (Juillet - Septembre)',
        '3': 'Trimestre 3 (Octobre - Décembre)',
        '4': 'Trimestre 4 (Janvier - Mars)'
      };
      const year = selectedPeriod === '4' ? fiscalYear + 1 : fiscalYear;
      return `${quarterMap[selectedPeriod as keyof typeof quarterMap]} ${year}`;
    } else {
      return getMonthsLabel(selectedPeriod, fiscalYear);
    }
  };

  const handleExportExcel = () => {
    const data = copros.map(copro => ({
      'Numéro': copro.numero,
      'Résidence': copro.residence,
      'Nombre de ventes': copro.transaction_count,
      'CA Total': copro.total_sales,
      'Honoraires HT': copro.total_fees,
      [`Rétrocessions ${config.title.replace('Copropriétés ', '')}`]: copro.total_retrocessions
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, config.title);

    const periodLabel = getFormattedPeriod().replace(/[\/\s]/g, '_');
    XLSX.writeFile(wb, `copro_${type}_${periodLabel}.xlsx`);
  };

  const handleExportPDF = async () => {
    try {
      setExporting(true);
      const tables = document.querySelectorAll('.copro-section');
      if (!tables.length) return;

      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });

      let currentY = 10;

      // Add title
      pdf.setFontSize(16);
      pdf.text(config.title, 10, currentY);
      currentY += 10;

      // Add period info
      pdf.setFontSize(12);
      const periodText = getFormattedPeriod();
      pdf.text(periodText, 10, currentY);
      currentY += 15;

      // Process each table section
      for (let i = 0; i < tables.length; i++) {
        const table = tables[i];
        
        // Capture the section
        const canvas = await html2canvas(table as HTMLElement, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff'
        });

        const imgData = canvas.toDataURL('image/png');
        const imgProps = pdf.getImageProperties(imgData);
        
        // Calculate dimensions to fit in page width
        const pdfWidth = pdf.internal.pageSize.getWidth() - 20; // 10mm margins on each side
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

        // Check if we need a new page
        if (currentY + pdfHeight > pdf.internal.pageSize.getHeight() - 10) {
          pdf.addPage();
          currentY = 10;
        }

        // Add the image
        pdf.addImage(imgData, 'PNG', 10, currentY, pdfWidth, pdfHeight);
        currentY += pdfHeight + 10; // Add some spacing between tables
      }

      const periodLabel = getFormattedPeriod().replace(/[\/\s]/g, '_');
      pdf.save(`copro_${type}_${periodLabel}.pdf`);
    } catch (error) {
      console.error('Error exporting PDF:', error);
    } finally {
      setExporting(false);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);

      let startDate: string;
      let endDate: string;

      if (period === 'year') {
        startDate = `${fiscalYear}-04-01`;
        endDate = `${fiscalYear + 1}-03-31`;
      } else if (period === 'quarter') {
        const quarterMap = {
          '1': { start: '04', end: '06' },
          '2': { start: '07', end: '09' },
          '3': { start: '10', end: '12' },
          '4': { start: '01', end: '03' }
        };
        const quarter = quarterMap[selectedPeriod as keyof typeof quarterMap];
        const year = selectedPeriod === '4' ? fiscalYear + 1 : fiscalYear;
        startDate = `${year}-${quarter.start}-01`;
        endDate = `${year}-${quarter.end}-${new Date(year, parseInt(quarter.end), 0).getDate()}`;
      } else {
        ({ startDate, endDate } = getMonthRangeDates(selectedPeriod, fiscalYear));
      }

      const ranges = getPeriodRanges(period, selectedPeriod, fiscalYear, startDate, endDate);

      let selectQuery = `
        id,
        property_number,
        seller,
        buyer,
        sale_price,
        fees_with_tax,
        fees_without_tax,
        status,
        compromise_date,
        transaction_agencies (
          agency
        ),
        transaction_negotiators (
          negotiator:negotiators (
            firstname,
            lastname
          ),
          role,
          percentage
        ),
        transaction_retrocessions (
          type,
          amount
        )
      `;

      if (type === 'lefeuvre') {
        selectQuery += `, copros (numero, residence)`;
      } else if (type === 'newco') {
        selectQuery += `, copros_newco (numero, residence)`;
      } else {
        selectQuery += `, copros_syndic (numero, residence)`;
      }

      const { data: transactions, error: transactionsError } = await supabase
        .from('transactions')
        .select(selectQuery)
        .not(config.foreignKey, 'is', null)
        .or(dateRangesFilter('compromise_date', ranges))
        .order('compromise_date', { ascending: false });

      if (transactionsError) throw transactionsError;

      const coproMap = new Map<string, CoproStats>();

      (transactions as any[] || []).forEach((t: any) => {
        const coproData = type === 'lefeuvre'
          ? t.copros
          : type === 'newco'
            ? t.copros_newco
            : t.copros_syndic;

        if (!coproData) return;

        const coproKey = coproData.numero;
        const existingCopro: CoproStats = coproMap.get(coproKey) || {
          numero: coproData.numero,
          residence: coproData.residence,
          transaction_count: 0,
          total_sales: 0,
          total_fees: 0,
          total_retrocessions: 0,
          transactions: []
        };

        const retrocessions = (t.transaction_retrocessions || [])
          .filter((r: any) => {
            if (type === 'lefeuvre') return r.type === 'retrocession_lefeuvre';
            if (type === 'newco') return r.type === 'retrocession_newco' || r.type === 'retrocession_lefeuvre';
            if (type === 'syndic') return r.type === 'retrocession_syndic' || r.type === 'retrocession_lefeuvre';
            return false;
          })
          .reduce((sum: number, r: any) => sum + r.amount, 0);

        existingCopro.transaction_count++;
        existingCopro.total_sales += t.sale_price;
        existingCopro.total_fees += t.fees_without_tax;
        existingCopro.total_retrocessions += retrocessions;
        existingCopro.transactions.push({
          id: t.id,
          property_number: t.property_number,
          seller: t.seller,
          buyer: t.buyer,
          sale_price: t.sale_price,
          fees_with_tax: t.fees_with_tax,
          fees_without_tax: t.fees_without_tax,
          status: t.status,
          compromise_date: t.compromise_date,
          agencies: (t.transaction_agencies || []).map((ta: any) => ta.agency),
          negotiators: (t.transaction_negotiators || []).map((tn: any) => ({
            name: `${tn.negotiator.firstname} ${tn.negotiator.lastname}`,
            role: tn.role,
            percentage: tn.percentage
          })),
          retrocessions: t.transaction_retrocessions || []
        });

        coproMap.set(coproKey, existingCopro);
      });

      const sortedCopros = Array.from(coproMap.values())
        .sort((a, b) => b.transaction_count - a.transaction_count);

      setCopros(sortedCopros);
    } catch (err: any) {
      console.error('Error loading copro data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getRetrocessionLabel = () => {
    switch (type) {
      case 'lefeuvre': return 'Rétrocessions Lefeuvre';
      case 'newco': return 'Rétrocessions NewCo';
      case 'syndic': return 'Rétrocessions Syndic';
    }
  };

  const getColorClasses = () => {
    switch (type) {
      case 'lefeuvre': return { bg: 'bg-blue-50', text: 'text-blue-600', bold: 'text-blue-900' };
      case 'newco': return { bg: 'bg-green-50', text: 'text-green-600', bold: 'text-green-900' };
      case 'syndic': return { bg: 'bg-purple-50', text: 'text-purple-600', bold: 'text-purple-900' };
    }
  };

  const colors = getColorClasses();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-600 rounded-lg">
        {error}
      </div>
    );
  }

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <button
          onClick={() => setIsManagementModalOpen(true)}
          className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <Settings size={18} />
          <span>Gérer les copropriétés</span>
        </button>

        <div className="flex items-center space-x-4">
          <button
            onClick={handleExportExcel}
            disabled={exporting}
            className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            <FileDown size={18} />
            <span>Excel</span>
          </button>
          <button
            onClick={handleExportPDF}
            disabled={exporting}
            className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <FileText size={18} />
            <span>PDF</span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden copro-section">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Numéro
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Résidence
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Nombre de ventes
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                CA Total
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Honoraires HT
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {getRetrocessionLabel()}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {copros.map((copro) => (
              <tr 
                key={copro.numero} 
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => {
                  setSelectedCopro(copro);
                  setIsModalOpen(true);
                }}
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {copro.numero}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {copro.residence}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {copro.transaction_count}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                  {formatCurrency(copro.total_sales)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                  {formatCurrency(copro.total_fees)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-purple-600">
                  {formatCurrency(copro.total_retrocessions)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {copros.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            Aucune transaction trouvée pour cette période
          </div>
        )}
      </div>

      {/* Modal de détail */}
      {isModalOpen && selectedCopro && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[200]">
          <div className="bg-white rounded-xl p-6 w-full max-w-6xl relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => {
                setIsModalOpen(false);
                setSelectedCopro(null);
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X size={20} />
            </button>

            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {selectedCopro.residence}
                </h2>
                <p className="text-sm text-gray-500">
                  Numéro: {selectedCopro.numero}
                </p>
              </div>

              <div className="grid grid-cols-4 gap-4">
                <div className={`${colors.bg} p-4 rounded-lg`}>
                  <p className={`text-sm font-medium ${colors.text}`}>Nombre de ventes</p>
                  <p className={`text-2xl font-bold ${colors.bold}`}>{selectedCopro.transaction_count}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-green-600">CA Total</p>
                  <p className="text-2xl font-bold text-green-900">{formatCurrency(selectedCopro.total_sales)}</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-purple-600">Honoraires HT</p>
                  <p className="text-2xl font-bold text-purple-900">{formatCurrency(selectedCopro.total_fees)}</p>
                </div>
                <div className="bg-amber-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-amber-600">Total {getRetrocessionLabel()}</p>
                  <p className="text-2xl font-bold text-amber-900">{formatCurrency(selectedCopro.total_retrocessions)}</p>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        N° Bien
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Vendeur
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Acquéreur
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Prix de vente
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Honoraires HT
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {getRetrocessionLabel().replace('Rétrocessions', 'Rétrocession')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Négociateur(s)
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {selectedCopro.transactions.map((transaction) => {
                      const retrocession = transaction.retrocessions
                        ?.filter(r => {
                          if (type === 'lefeuvre') return r.type === 'retrocession_lefeuvre';
                          if (type === 'newco') return r.type === 'retrocession_newco' || r.type === 'retrocession_lefeuvre';
                          if (type === 'syndic') return r.type === 'retrocession_syndic' || r.type === 'retrocession_lefeuvre';
                          return false;
                        })
                        ?.reduce((sum, r) => sum + r.amount, 0) || 0;

                      return (
                        <tr key={transaction.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {transaction.property_number}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(transaction.compromise_date).toLocaleDateString('fr-FR')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {transaction.seller}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {transaction.buyer}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                            {formatCurrency(transaction.sale_price)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                            {formatCurrency(transaction.fees_without_tax)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-amber-600">
                            {formatCurrency(retrocession)}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {transaction.negotiators.map((n, i) => (
                              <div key={i}>
                                {n.name} ({n.role} - {n.percentage}%)
                              </div>
                            ))}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      <CoproManagementModal
        isOpen={isManagementModalOpen}
        onClose={() => setIsManagementModalOpen(false)}
        type={type}
        onSuccess={loadData}
      />
    </>
  );
}